<script setup>
import { ref } from 'vue';
import { useHeightTransition } from './use_height_transition.js';

const props = defineProps({
  title:      { type: String, required: true },
  open:       { type: Boolean, default: false },
  compact:    { type: Boolean, default: false },
  fixed:      { type: Boolean, default: false },
  full_width: { type: Boolean, default: false },
});

const emit = defineEmits(['toggle']);

function toggle() {
  emit('toggle');
}

// Animate the body's height between 0 and its natural content height on
// open/close, rather than the instant v-if snap.
const { beforeEnter, enter, afterEnter, beforeLeave, leave, afterLeave } = useHeightTransition();
</script>

<template>
  <div
    class="panel"
    :class="{ compact, open, fixed, full_width }"
  >
    <div class="panel-clip">
      <div
        class="panel-header"
        :tabindex="fixed ? undefined : 0"
        :role="fixed ? undefined : 'button'"
        :aria-expanded="fixed ? undefined : open"
        @click="!fixed && toggle()"
        @keydown.enter="!fixed && toggle()"
        @keydown.space.prevent="!fixed && toggle()"
      >
        <span>{{ title }}</span>
        <span v-if="!fixed" class="toggle-icon" :class="{ collapsed: !open }">▼</span>
      </div>
      <Transition
        @before-enter="beforeEnter"
        @enter="enter"
        @after-enter="afterEnter"
        @before-leave="beforeLeave"
        @leave="leave"
        @after-leave="afterLeave"
      >
        <div class="panel-body" v-if="open">
          <slot />
        </div>
      </Transition>
    </div>
  </div>
</template>

<style scoped>

/* ═══════════════════════════════════════════════
   Base panel — all sizing lives here, not in parent
   ═══════════════════════════════════════════════ */

.panel {
  background: var(--color-bg);
  border-radius: var(--border-radius);
  border: var(--border-width) solid transparent;
  padding: 0;
  margin: 0;
  box-sizing: border-box;
  /* Actual border width in effect, so .panel-clip's inset can match it —
     overridden to the thicker value where .primary-panel applies
     --border-strong instead of the default border. */
  --panel-border-width: var(--border-width);

  /* Default: content-sized, centered */
  flex: 0 1 auto;
  /* `min-content` looks like the "don't get silly narrow" safety net this
     was meant to be, but for a panel holding anything wide and non-wrapping
     (a canvas, a `white-space: nowrap` table) it explicitly computes to
     that content's full natural width — a floor the panel can never shrink
     below, on any viewport, no matter what min-width overrides exist
     further down the tree. Content that needs a minimum comfortable width
     already gets one from its own CSS (.field, table cell widths, ...); the
     panel itself doesn't need to also enforce one. */
  min-width: 0;
  max-width: 100%;
  align-self: center;
}

/* Clips the header/body to the panel's rounded corners, as a separate
   element from `.panel` so the primary panel's box-shadow (on `.panel`)
   never sits inside an `overflow: hidden` box. `.panel-clip` is inset
   from `.panel`'s outer edge by the border width, so its radius must be
   shrunk by that same amount to stay concentric — otherwise it clips to
   the outer curve and `.panel`'s own background peeks through the corner. */
.panel-clip {
  border-radius: calc(var(--border-radius) - var(--panel-border-width));
  overflow: hidden;
}

/* Show border when panel has content */
.panel:has(.panel-body),
.panel.open {
  border-color: var(--color-border);
}

/* ── Full width panels (graph, trajectory) ── */
.panel.full_width {
  flex: 0 0 calc(100% - 2rem);
  width: calc(100% - 2rem);
  max-width: calc(100% - 2rem);
  align-self: center;
  margin: 0;
}

/* ── Compact panels (closed buttons) ── */
.panel.compact {
  flex: 0 0 auto;
}

/* Sized to its own content, not stretched to fill the row (no flex-grow) —
   an open compact panel sits inline next to any still-closed sibling
   buttons at its natural width instead of eating the rest of the row. */
.panel.compact.open {
  flex: 0 1 auto;
}

/* ═══════════════════════════════════════════════
   Panel header
   ═══════════════════════════════════════════════ */

.panel-header {
  position: relative;
  text-align: center;
  padding: var(--header-padding-y) var(--header-padding-x);
  padding-right: 2.5rem;
  background: var(--color-primary);
  color: var(--color-text-light);
  cursor: pointer;
  user-select: none;
  font-weight: var(--font-weight-semibold);
  /* Flush against .panel-clip's edge, so its own curve must match
     .panel-clip's (border-inset) radius, not .panel's outer radius —
     otherwise a sliver of .panel's background shows through the gap
     between the two differently-curved corners. */
  border-radius: calc(var(--border-radius) - var(--panel-border-width))
    calc(var(--border-radius) - var(--panel-border-width)) 0 0;
  width: 100%;
  margin: 0;
  box-sizing: border-box;
}

.panel-header:hover {
  background: var(--color-primary-hover);
}

/* Compact closed: auto-width header */
.panel.compact .panel-header {
  width: auto;
  min-width: 150px;
  white-space: nowrap;
}

.panel.compact.open .panel-header {
  width: 100%;
}

/* Fixed panel: no hover, no cursor */
.panel.fixed .panel-header {
  cursor: default;
}

.panel.fixed .panel-header:hover {
  background: var(--color-primary);
}

/* Toggle icon */
.toggle-icon {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  transition: transform 0.2s;
  font-size: var(--font-size-sm);
}

.toggle-icon.collapsed {
  transform: translateY(-50%) rotate(90deg);
}

/* ═══════════════════════════════════════════════
   Panel body
   ═══════════════════════════════════════════════ */

.panel-body {
  padding: var(--space-lg);
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
}

.panel-body select {
  max-width: 100%;
  min-width: 120px;
  flex-shrink: 0;
  width: auto;
}
</style>

